import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { Field, Form, Formik, FormikProps } from 'formik';
import { IDataTableFilters, IDataTableRef } from 'src/components/DataTable/shared/types';
import TradeMarksField from 'src/components/Fields/TradeMarksField';
import SegmentedControl from 'src/components/SegmentedControl/SegmentedControl';
import { vendorUrlRegexPath, withPimURL } from 'src/helpers/service';
import { cloneObject, isEqual, omit, pick } from 'src/helpers/utils';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import Container from 'src/pages/inventory/components/category-container';
import useAcceptedFlashProductsColumns from '../helpers/colum-definitions/use-accepted-flash-products-columns';
import {
  flashProductTransitionOptions,
  IIncludedFlashProductsFilterValues,
  includedFlashProductsFilterValuesInitialValues,
  includedFlashProductsFilterValuesValidationSchema,
} from '../helpers/constants';
import useFlashDiscountPeriodTypes from '../services/hooks/use-flash-discount-period-types';
import { FlashProductTransition } from '../services/models/flash-discount';

let outerCategories: any[] = [];

interface IIncludedFlashProductsFilterProps {
  dataTableRef: React.RefObject<IDataTableRef>;
  /** Filtre transition değeri değiştiğinde çağrılır (örn. sayfada checkbox/buton gizlemek için). */
  onTransitionChange?: (transition: FlashProductTransition | null) => void;
}

export default function IncludedFlashProductsFilter(props: IIncludedFlashProductsFilterProps) {
  const { dataTableRef, onTransitionChange } = props;

  const [filters, setFilters] = useState<IDataTableFilters>(includedFlashProductsFilterValuesInitialValues);

  const [updatedCategory, setUpdatedCategory] = useState<any>([]);
  const formikRef = useRef<FormikProps<IIncludedFlashProductsFilterValues>>(null);

  const t = translate(Translations);
  const translation = 'pages.flashProducts.included.filter';

  const flashDiscountPeriodTypes = useFlashDiscountPeriodTypes();

  const periodTypeOptions = useMemo(() => {
    const options = flashDiscountPeriodTypes.data?.map((period) => ({
      value: String(period.id),
      label: period.title,
    }));

    return [
      {
        value: null,
        label: 'Tümü',
      },
      ...(options || []),
    ] as { value: number | null; label: string }[];
  }, [flashDiscountPeriodTypes.data]);

  const findValue = (arr: any, val: any, checked: any) => {
    for (let obj of arr) {
      if (obj.id === val) {
        obj.checked = checked;
        return arr;
      }
      if (obj.children) {
        let result: any = findValue(obj.children, val, checked);
        if (result) {
          return arr;
        }
      }
    }
    return undefined;
  };

  const onChangeCategoryFunc = (currentNode: any, selectedNodes: any) => {
    var cpyUpdatedCategory = [...updatedCategory];
    outerCategories = selectedNodes?.map((i: any) => i.id);
    formikRef.current?.setFieldValue('categoryId', outerCategories);
    setUpdatedCategory(findValue(cpyUpdatedCategory, currentNode.id, currentNode.checked));
  };

  const generateUpdateCategories = useCallback((data: any[], selectedIds = []): any[] => {
    return data?.map((category: any) => ({
      id: category?.id,
      label: category?.name,
      value: category?.name,
      children: category?.subs?.length ? generateUpdateCategories(category?.subs) : [],
      checked: selectedIds?.includes(category?.id),
    }));
  }, []);

  const disableForm = useMemo(() => {
    return flashDiscountPeriodTypes.status !== 'success';
  }, [flashDiscountPeriodTypes.status]);

  const getCategoryQueryKey = useMemo(() => ['get-categories'], []);

  const getCategories = useQuery({
    queryKey: getCategoryQueryKey,
    queryFn: async () => {
      const response = await axios.get(withPimURL(vendorUrlRegexPath(':vendorId/category/tree/list'))).then((res) => res.data);
      setUpdatedCategory(generateUpdateCategories(response || []));
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const handleFilterChange = (values: Partial<IIncludedFlashProductsFilterValues>) => {
    const trimmedValues = Object.entries(values).reduce((acc: any, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});

    const newFiltersDispatch = (prev: IDataTableFilters) => {
      return {
        ...prev,
        ...trimmedValues,
      };
    };

    const nextFilters = newFiltersDispatch(filters);
    setFilters(newFiltersDispatch);
    dataTableRef?.current?.setFilters(newFiltersDispatch);
    onTransitionChange?.(nextFilters.transition ?? null);
  };

  const handleSubmit = (_values: IIncludedFlashProductsFilterValues) => {
    const values = Object.assign({}, _values);
    if (values.transition) {
      delete (values as any).transition;
    }

    if (values.periodTypeId) {
      delete (values as any).periodTypeId;
    }

    handleFilterChange(values);
  };

  useEffect(() => {
    onTransitionChange?.(filters.transition ?? null);
  }, []);

  const filterIsChanged = useCallback(
    (
      filterValue?: any,
      deleteInObj: ('tab' | 'page' | 'sort')[] = ['tab', 'page', 'sort'],
      onlyControl?: (keyof IIncludedFlashProductsFilterValues)[],
      doNotControl?: (keyof IIncludedFlashProductsFilterValues)[],
    ) => {
      if (!filterValue) filterValue = filters;

      const cloneFilterValue = cloneObject(filterValue);
      const cloneInitialFilterValues = cloneObject(includedFlashProductsFilterValuesInitialValues);

      (deleteInObj || []).forEach((key) => {
        delete cloneFilterValue[key];
        delete cloneInitialFilterValues[key];
      });

      let a = cloneInitialFilterValues;
      let b = cloneFilterValue;

      if (onlyControl) {
        a = pick(cloneInitialFilterValues, onlyControl as string[]);
        b = pick(cloneFilterValue, onlyControl as string[]);
      }

      if (doNotControl) {
        a = omit(cloneInitialFilterValues, doNotControl as string[]);
        b = omit(cloneFilterValue, doNotControl as string[]);
      }

      return !isEqual(a, b);
    },
    [filters],
  );

  return (
    <>
      <Formik
        initialValues={includedFlashProductsFilterValuesInitialValues}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        validationSchema={includedFlashProductsFilterValuesValidationSchema}
      >
        {({ values, setFieldValue, resetForm, isValid, errors, touched, handleBlur }) => (
          <Form className="form">
            <div className="row">
              {/** Left Side */}
              <div className="col-md-9">
                <div className="row">
                  <div className="mb-5 col-md-4">
                    <Field
                      id="searchTerm"
                      name="searchTerm"
                      type="text"
                      value={values.searchTerm || ''}
                      className={clsx('form-control form-control-solid', { 'is-invalid': errors.searchTerm && touched.searchTerm })}
                      placeholder="Ürün Adı, Stok Kodu, Barkod, Ürün Kodu"
                      disabled={disableForm}
                    />
                    {errors.searchTerm && touched.searchTerm && <div className="invalid-feedback position-absolute -bottom-20">{errors.searchTerm}</div>}
                  </div>

                  <div className="mb-5 col-md-4">
                    <Container
                      keepTreeOnSearch
                      clearSearchOnChange={true}
                      texts={{ placeholder: 'Kategori seçiniz', noMatches: 'Arama sonucu bulunamadı' }}
                      className={`w-100 pb-0 pt-0 pl-0 ps-0 category`}
                      onChange={onChangeCategoryFunc}
                      data={updatedCategory ? updatedCategory : []}
                      disabled={disableForm}
                    />
                    {errors.categoryId && touched.categoryId && <div className="invalid-feedback position-absolute -bottom-20">{errors.categoryId}</div>}
                  </div>

                  <div className="mb-5 col-md-4">
                    <TradeMarksField
                      fetchFromMCAPI
                      searchFromMCAPI
                      queryKey="flash-products-brand"
                      defaultValue={values.brandId as any}
                      placeholder="Marka"
                      hideSelectedOptions={true}
                      onChange={(value) => setFieldValue('brandId', value)}
                      isMulti
                      className={clsx('', { 'is-invalid': errors.brandId && touched.brandId })}
                      isDisabled={disableForm}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          backgroundColor: '#F5F8FA',
                          borderWidth: 0,
                          width: '100%',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                        }),
                      }}
                    />
                    {errors.brandId && touched.brandId && <div className="invalid-feedback position-absolute -bottom-20">{errors.brandId}</div>}
                  </div>
                </div>
              </div>

              {/** Right Side */}
              <div className="col-md-3">
                <div className="row d-flex justify-content-center mb-5">
                  <div className="col-md-6 col-lg-6 col-sm-6">
                    <button
                      type="submit"
                      className="btn btn-light-primary w-100"
                      disabled={
                        (disableForm ||
                          (!filterIsChanged(values) && isEqual(omit(filters, ['transition', 'periodTypeId']), omit(values, ['transition', 'periodTypeId']))) ||
                          (filterIsChanged(values) && isEqual(omit(filters, ['transition', 'periodTypeId']), omit(values, ['transition', 'periodTypeId'])))) as boolean
                      }
                      title={!filterIsChanged(values) ? 'Filtreler değiştirilmedi' : undefined}
                    >
                      {t(`${translation}.search`)}
                    </button>
                  </div>
                  <div className="col-md-6 col-lg-6 col-sm-6">
                    <button
                      type="button"
                      onClick={() => {
                        resetForm();

                        const initialValues = cloneObject(includedFlashProductsFilterValuesInitialValues);
                        delete initialValues.transition;
                        handleFilterChange(initialValues);

                        setUpdatedCategory(generateUpdateCategories(getCategories.data || []));
                      }}
                      className="btn btn-secondary w-100"
                      disabled={!filterIsChanged(values)}
                      title={!filterIsChanged(values) ? 'Filtreler değiştirilmedi' : undefined}
                    >
                      {t(`${translation}.clean`)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <div className="row mt-5">
        <div className="col-md-6 d-flex align-items-center justify-content-start">
          <SegmentedControl
            disabled={disableForm}
            options={flashProductTransitionOptions}
            forceValue={filters.transition}
            onValueChange={(value) => handleFilterChange({ transition: value as FlashProductTransition })}
          />
        </div>

        <div className="col-md-6 d-flex align-items-center justify-content-end">
          <SegmentedControl
            disabled={disableForm}
            options={periodTypeOptions}
            forceValue={filters.periodTypeId}
            onValueChange={(value) => handleFilterChange({ periodTypeId: value ? String(value) : null })}
          />
        </div>
      </div>
    </>
  );
}
